import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { zatcaService } from '../../services/api'
import { ZatcaStatus, ZatcaError, ZatcaWarning } from '../../types/api'
import { isApiError } from '../../utils/api'

interface ZatcaState {
  // QR Code Management
  qrCodes: Record<string, {
    invoice_id: string
    qr_code: string
    format: string
    version: number
    data_encoded: string
    generated_at: string
  }>
  currentQrCode: string | null
  qrGenerating: boolean

  // Compliance Status
  complianceStatuses: Record<string, ZatcaStatus>
  currentComplianceStatus: ZatcaStatus | null
  complianceLoading: boolean

  // Validation Results
  validationResults: Record<string, {
    compliant: boolean
    errors: ZatcaError[]
    warnings: ZatcaWarning[]
    compliance_score: number
    required_fields: Array<{
      field: string
      status: 'valid' | 'invalid' | 'missing'
      message?: string
    }>
  }>
  validationLoading: boolean

  // Submission Management
  submissions: Array<{
    id: string
    invoice_id: string
    invoice_number: string
    status: 'PENDING' | 'SUBMITTED' | 'CLEARED' | 'REJECTED'
    submitted_at: string
    cleared_at?: string
    rejected_at?: string
    rejection_reason?: string
    zatca_uuid?: string
    errors: ZatcaError[]
    warnings: ZatcaWarning[]
  }>
  submissionHistory: Array<{
    id: string
    invoice_id: string
    invoice_number: string
    status: string
    submitted_at: string
    cleared_at?: string
    errors: ZatcaError[]
    warnings: ZatcaWarning[]
  }>
  submissionLoading: boolean
  submissionStatus: Record<string, {
    status: 'PENDING' | 'SUBMITTED' | 'CLEARED' | 'REJECTED'
    submitted_at?: string
    cleared_at?: string
    rejected_at?: string
    rejection_reason?: string
    zatca_uuid?: string
  }>

  // Certificate Management
  certificate: {
    certificate_id?: string
    serial_number?: string
    issuer?: string
    subject?: string
    valid_from?: string
    valid_to?: string
    is_valid: boolean
    days_until_expiry?: number
    environment: 'sandbox' | 'production'
  } | null
  certificateValidation: {
    valid: boolean
    errors: string[]
    warnings: string[]
    expiry_status: 'valid' | 'expiring_soon' | 'expired'
  } | null
  certificateLoading: boolean

  // Configuration
  configuration: {
    environment: 'sandbox' | 'production'
    api_url: string
    certificate_configured: boolean
    auto_submit: boolean
    auto_clearance: boolean
    compliance_enabled: boolean
    settings: Record<string, any>
  } | null

  // System Health
  systemHealth: {
    zatca_api_status: 'online' | 'offline' | 'degraded'
    certificate_status: 'valid' | 'expiring' | 'expired' | 'missing'
    last_successful_submission?: string
    pending_submissions: number
    failed_submissions_last_24h: number
    system_load: 'low' | 'medium' | 'high'
  } | null

  // Reports and Analytics
  complianceReport: {
    total_invoices: number
    compliant_invoices: number
    non_compliant_invoices: number
    compliance_rate: number
    common_errors: Array<{
      error_code: string
      error_message: string
      count: number
    }>
    monthly_compliance: Array<{
      month: string
      total: number
      compliant: number
      rate: number
    }>
  } | null

  errorStatistics: {
    total_errors: number
    resolved_errors: number
    pending_errors: number
    error_breakdown: Array<{
      code: string
      message: string
      count: number
      severity: 'error' | 'warning'
      resolution_rate: number
    }>
  } | null

  // Bulk Operations
  bulkOperationStatus: {
    operation: 'validate' | 'submit' | null
    progress: number
    completed: number
    failed: number
    total: number
    results: Array<{
      invoice_id: string
      success: boolean
      error?: string
    }>
  }

  // Connection Status
  connectionStatus: {
    connected: boolean
    response_time: number
    api_version: string
    environment: string
    errors: string[]
    last_tested: string
  } | null

  // Loading States
  loading: boolean
  error: string | null
}

const initialState: ZatcaState = {
  qrCodes: {},
  currentQrCode: null,
  qrGenerating: false,
  complianceStatuses: {},
  currentComplianceStatus: null,
  complianceLoading: false,
  validationResults: {},
  validationLoading: false,
  submissions: [],
  submissionHistory: [],
  submissionLoading: false,
  submissionStatus: {},
  certificate: null,
  certificateValidation: null,
  certificateLoading: false,
  configuration: null,
  systemHealth: null,
  complianceReport: null,
  errorStatistics: null,
  bulkOperationStatus: {
    operation: null,
    progress: 0,
    completed: 0,
    failed: 0,
    total: 0,
    results: []
  },
  connectionStatus: null,
  loading: false,
  error: null
}

// Async thunks
export const generateQRCode = createAsyncThunk(
  'zatca/generateQRCode',
  async ({ invoiceId, options }: { 
    invoiceId: string; 
    options?: { version?: 1 | 2; format?: 'base64' | 'svg' | 'png'; size?: number } 
  }, { rejectWithValue }) => {
    try {
      const response = await zatcaService.generateQRCode(invoiceId, options)
      return { invoice_id: invoiceId, ...response.data, generated_at: new Date().toISOString() }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to generate QR code')
    }
  }
)

export const validateQRCode = createAsyncThunk(
  'zatca/validateQRCode',
  async (qrData: string, { rejectWithValue }) => {
    try {
      const response = await zatcaService.validateQRCode(qrData)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to validate QR code')
    }
  }
)

export const validateInvoice = createAsyncThunk(
  'zatca/validateInvoice',
  async (invoiceId: string, { rejectWithValue }) => {
    try {
      const response = await zatcaService.validateInvoice(invoiceId)
      return { invoice_id: invoiceId, ...response.data }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to validate invoice')
    }
  }
)

export const getComplianceStatus = createAsyncThunk(
  'zatca/getComplianceStatus',
  async (invoiceId: string, { rejectWithValue }) => {
    try {
      const response = await zatcaService.getComplianceStatus(invoiceId)
      return { invoice_id: invoiceId, status: response.data }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get compliance status')
    }
  }
)

export const submitToZatca = createAsyncThunk(
  'zatca/submitToZatca',
  async ({ invoiceId, options }: { 
    invoiceId: string; 
    options?: { clearance?: boolean; reporting?: boolean; test_mode?: boolean } 
  }, { rejectWithValue }) => {
    try {
      const response = await zatcaService.submitToZatca(invoiceId, options)
      return { invoice_id: invoiceId, ...response.data }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to submit to ZATCA')
    }
  }
)

export const getSubmissionStatus = createAsyncThunk(
  'zatca/getSubmissionStatus',
  async (submissionId: string, { rejectWithValue }) => {
    try {
      const response = await zatcaService.getSubmissionStatus(submissionId)
      return { submission_id: submissionId, ...response.data }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get submission status')
    }
  }
)

export const resubmitInvoice = createAsyncThunk(
  'zatca/resubmitInvoice',
  async (invoiceId: string, { rejectWithValue }) => {
    try {
      const response = await zatcaService.resubmitInvoice(invoiceId)
      return { invoice_id: invoiceId, ...response.data }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to resubmit invoice')
    }
  }
)

export const uploadCertificate = createAsyncThunk(
  'zatca/uploadCertificate',
  async (data: {
    certificate: File
    private_key: File
    password?: string
    environment: 'sandbox' | 'production'
  }, { rejectWithValue }) => {
    try {
      const response = await zatcaService.uploadCertificate(data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to upload certificate')
    }
  }
)

export const getCertificateInfo = createAsyncThunk(
  'zatca/getCertificateInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await zatcaService.getCertificateInfo()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get certificate info')
    }
  }
)

export const validateCertificate = createAsyncThunk(
  'zatca/validateCertificate',
  async (_, { rejectWithValue }) => {
    try {
      const response = await zatcaService.validateCertificate()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to validate certificate')
    }
  }
)

export const getConfiguration = createAsyncThunk(
  'zatca/getConfiguration',
  async (_, { rejectWithValue }) => {
    try {
      const response = await zatcaService.getConfiguration()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get configuration')
    }
  }
)

export const updateConfiguration = createAsyncThunk(
  'zatca/updateConfiguration',
  async (config: {
    environment?: 'sandbox' | 'production'
    auto_submit?: boolean
    auto_clearance?: boolean
    compliance_enabled?: boolean
    settings?: Record<string, any>
  }, { rejectWithValue }) => {
    try {
      await zatcaService.updateConfiguration(config)
      return config
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update configuration')
    }
  }
)

export const testConnection = createAsyncThunk(
  'zatca/testConnection',
  async (_, { rejectWithValue }) => {
    try {
      const response = await zatcaService.testConnection()
      return { ...response.data, last_tested: new Date().toISOString() }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to test connection')
    }
  }
)

export const getSystemHealth = createAsyncThunk(
  'zatca/getSystemHealth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await zatcaService.getSystemHealth()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get system health')
    }
  }
)

export const getSubmissionHistory = createAsyncThunk(
  'zatca/getSubmissionHistory',
  async (filters: {
    date_from?: string
    date_to?: string
    status?: string
    limit?: number
  } = {}, { rejectWithValue }) => {
    try {
      const response = await zatcaService.getSubmissionHistory(filters)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get submission history')
    }
  }
)

export const getComplianceReport = createAsyncThunk(
  'zatca/getComplianceReport',
  async (period: { date_from?: string; date_to?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await zatcaService.getComplianceReport(period)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get compliance report')
    }
  }
)

export const getErrorStatistics = createAsyncThunk(
  'zatca/getErrorStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await zatcaService.getErrorStatistics()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get error statistics')
    }
  }
)

export const bulkValidateInvoices = createAsyncThunk(
  'zatca/bulkValidate',
  async (invoiceIds: string[], { rejectWithValue }) => {
    try {
      const response = await zatcaService.bulkValidate(invoiceIds)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk validate invoices')
    }
  }
)

export const bulkSubmitInvoices = createAsyncThunk(
  'zatca/bulkSubmit',
  async ({ invoiceIds, options }: { 
    invoiceIds: string[]; 
    options?: { clearance?: boolean; test_mode?: boolean } 
  }, { rejectWithValue }) => {
    try {
      const response = await zatcaService.bulkSubmit(invoiceIds, options)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk submit invoices')
    }
  }
)

// Create slice
const zatcaSlice = createSlice({
  name: 'zatca',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentQrCode: (state, action: PayloadAction<string | null>) => {
      state.currentQrCode = action.payload
    },
    setCurrentComplianceStatus: (state, action: PayloadAction<ZatcaStatus | null>) => {
      state.currentComplianceStatus = action.payload
    },
    clearQrCodes: (state) => {
      state.qrCodes = {}
      state.currentQrCode = null
    },
    clearValidationResults: (state) => {
      state.validationResults = {}
    },
    clearComplianceStatuses: (state) => {
      state.complianceStatuses = {}
      state.currentComplianceStatus = null
    },
    clearSubmissions: (state) => {
      state.submissions = []
      state.submissionStatus = {}
    },
    resetBulkOperationStatus: (state) => {
      state.bulkOperationStatus = {
        operation: null,
        progress: 0,
        completed: 0,
        failed: 0,
        total: 0,
        results: []
      }
    },
    updateBulkProgress: (state, action: PayloadAction<{
      progress: number
      completed: number
      failed: number
    }>) => {
      state.bulkOperationStatus = {
        ...state.bulkOperationStatus,
        ...action.payload
      }
    }
  },
  extraReducers: (builder) => {
    // Generate QR Code
    builder
      .addCase(generateQRCode.pending, (state) => {
        state.qrGenerating = true
        state.error = null
      })
      .addCase(generateQRCode.fulfilled, (state, action) => {
        state.qrGenerating = false
        state.qrCodes[action.payload.invoice_id] = action.payload
        state.currentQrCode = action.payload.qr_code
      })
      .addCase(generateQRCode.rejected, (state, action) => {
        state.qrGenerating = false
        state.error = action.payload as string
      })

    // Validate Invoice
    builder
      .addCase(validateInvoice.pending, (state) => {
        state.validationLoading = true
        state.error = null
      })
      .addCase(validateInvoice.fulfilled, (state, action) => {
        state.validationLoading = false
        const { invoice_id, ...validationData } = action.payload
        state.validationResults[invoice_id] = validationData
      })
      .addCase(validateInvoice.rejected, (state, action) => {
        state.validationLoading = false
        state.error = action.payload as string
      })

    // Get Compliance Status
    builder
      .addCase(getComplianceStatus.pending, (state) => {
        state.complianceLoading = true
        state.error = null
      })
      .addCase(getComplianceStatus.fulfilled, (state, action) => {
        state.complianceLoading = false
        const { invoice_id, status } = action.payload
        state.complianceStatuses[invoice_id] = status
        state.currentComplianceStatus = status
      })
      .addCase(getComplianceStatus.rejected, (state, action) => {
        state.complianceLoading = false
        state.error = action.payload as string
      })

    // Submit to ZATCA
    builder
      .addCase(submitToZatca.pending, (state) => {
        state.submissionLoading = true
        state.error = null
      })
      .addCase(submitToZatca.fulfilled, (state, action) => {
        state.submissionLoading = false
        const submission = {
          id: action.payload.uuid || '',
          invoice_id: action.payload.invoice_id,
          invoice_number: '',
          status: (() => {
            const clearanceStatus = action.payload.clearance_status;
            switch (clearanceStatus) {
              case 'NOT_CLEARED':
                return 'REJECTED' as const;
              case 'REPORTED':
                return 'SUBMITTED' as const;
              default:
                return (clearanceStatus || 'SUBMITTED') as 'PENDING' | 'SUBMITTED' | 'CLEARED' | 'REJECTED';
            }
          })(),
          submitted_at: action.payload.submission_date,
          zatca_uuid: action.payload.uuid,
          errors: action.payload.validation_results?.errors || [],
          warnings: action.payload.validation_results?.warnings || []
        }
        state.submissions.unshift(submission)
      })
      .addCase(submitToZatca.rejected, (state, action) => {
        state.submissionLoading = false
        state.error = action.payload as string
      })

    // Certificate Management
    builder
      .addCase(uploadCertificate.pending, (state) => {
        state.certificateLoading = true
        state.error = null
      })
      .addCase(uploadCertificate.fulfilled, (state, action) => {
        state.certificateLoading = false
        state.certificate = {
          ...action.payload,
          is_valid: true,
          environment: 'sandbox'
        }
      })
      .addCase(uploadCertificate.rejected, (state, action) => {
        state.certificateLoading = false
        state.error = action.payload as string
      })

    builder
      .addCase(getCertificateInfo.fulfilled, (state, action) => {
        state.certificate = action.payload
      })
      .addCase(getCertificateInfo.rejected, (state, action) => {
        state.error = action.payload as string
      })

    builder
      .addCase(validateCertificate.fulfilled, (state, action) => {
        state.certificateValidation = action.payload
      })
      .addCase(validateCertificate.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Configuration
    builder
      .addCase(getConfiguration.fulfilled, (state, action) => {
        state.configuration = action.payload
      })
      .addCase(getConfiguration.rejected, (state, action) => {
        state.error = action.payload as string
      })

    builder
      .addCase(updateConfiguration.fulfilled, (state, action) => {
        if (state.configuration) {
          state.configuration = { ...state.configuration, ...action.payload }
        }
      })
      .addCase(updateConfiguration.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // System Health and Status
    builder
      .addCase(testConnection.fulfilled, (state, action) => {
        state.connectionStatus = action.payload
      })
      .addCase(testConnection.rejected, (state, action) => {
        state.error = action.payload as string
      })

    builder
      .addCase(getSystemHealth.fulfilled, (state, action) => {
        state.systemHealth = action.payload
      })
      .addCase(getSystemHealth.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Reports and Analytics
    builder
      .addCase(getSubmissionHistory.fulfilled, (state, action) => {
        state.submissionHistory = action.payload
      })
      .addCase(getSubmissionHistory.rejected, (state, action) => {
        state.error = action.payload as string
      })

    builder
      .addCase(getComplianceReport.fulfilled, (state, action) => {
        state.complianceReport = action.payload
      })
      .addCase(getComplianceReport.rejected, (state, action) => {
        state.error = action.payload as string
      })

    builder
      .addCase(getErrorStatistics.fulfilled, (state, action) => {
        state.errorStatistics = action.payload
      })
      .addCase(getErrorStatistics.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Bulk Operations
    builder
      .addCase(bulkValidateInvoices.pending, (state) => {
        state.bulkOperationStatus.operation = 'validate'
      })
      .addCase(bulkValidateInvoices.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.validated,
          failed: action.payload.failed,
          total: action.payload.total,
          results: action.payload.results.map(r => ({
            invoice_id: r.invoice_id,
            success: r.compliant,
            error: r.errors.length > 0 ? r.errors[0].message : undefined
          }))
        }
      })
      .addCase(bulkValidateInvoices.rejected, (state, action) => {
        state.bulkOperationStatus.operation = null
        state.error = action.payload as string
      })

    builder
      .addCase(bulkSubmitInvoices.pending, (state) => {
        state.bulkOperationStatus.operation = 'submit'
      })
      .addCase(bulkSubmitInvoices.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.submitted,
          failed: action.payload.failed,
          total: action.payload.total,
          results: action.payload.results
        }
      })
      .addCase(bulkSubmitInvoices.rejected, (state, action) => {
        state.bulkOperationStatus.operation = null
        state.error = action.payload as string
      })
  },
})

// Export actions
export const {
  clearError,
  setCurrentQrCode,
  setCurrentComplianceStatus,
  clearQrCodes,
  clearValidationResults,
  clearComplianceStatuses,
  clearSubmissions,
  resetBulkOperationStatus,
  updateBulkProgress
} = zatcaSlice.actions

// Selectors
export const selectZatcaQrCodes = (state: { zatca: ZatcaState }) => state.zatca.qrCodes
export const selectCurrentQrCode = (state: { zatca: ZatcaState }) => state.zatca.currentQrCode
export const selectComplianceStatuses = (state: { zatca: ZatcaState }) => state.zatca.complianceStatuses
export const selectCurrentComplianceStatus = (state: { zatca: ZatcaState }) => state.zatca.currentComplianceStatus
export const selectValidationResults = (state: { zatca: ZatcaState }) => state.zatca.validationResults
export const selectZatcaCertificate = (state: { zatca: ZatcaState }) => state.zatca.certificate
export const selectZatcaConfiguration = (state: { zatca: ZatcaState }) => state.zatca.configuration
export const selectSystemHealth = (state: { zatca: ZatcaState }) => state.zatca.systemHealth
export const selectSubmissionHistory = (state: { zatca: ZatcaState }) => state.zatca.submissionHistory
export const selectComplianceReport = (state: { zatca: ZatcaState }) => state.zatca.complianceReport
export const selectErrorStatistics = (state: { zatca: ZatcaState }) => state.zatca.errorStatistics
export const selectConnectionStatus = (state: { zatca: ZatcaState }) => state.zatca.connectionStatus
export const selectZatcaBulkOperationStatus = (state: { zatca: ZatcaState }) => state.zatca.bulkOperationStatus
export const selectZatcaLoading = (state: { zatca: ZatcaState }) => state.zatca.loading
export const selectZatcaError = (state: { zatca: ZatcaState }) => state.zatca.error

// Helper selectors
export const selectInvoiceQrCode = (invoiceId: string) => (state: { zatca: ZatcaState }) => 
  state.zatca.qrCodes[invoiceId]

export const selectInvoiceComplianceStatus = (invoiceId: string) => (state: { zatca: ZatcaState }) => 
  state.zatca.complianceStatuses[invoiceId]

export const selectInvoiceValidationResult = (invoiceId: string) => (state: { zatca: ZatcaState }) => 
  state.zatca.validationResults[invoiceId]

export default zatcaSlice.reducer 